/**
 * Progetti (docs/adr/ADR-025, decisions of 26/10/2026): the internal work of
 * K-Rev, by area (app evolutions, marketing, website…), with a PM, a budget of
 * the costs, an objective and objective indicators measured over time.
 *
 * Who sees and changes the progetti of an area is chosen by the administrator
 * of the module «Commesse e progetti», user by user, also for users without
 * the module; the PM always sees and updates their own progetto.
 */
import { z } from 'zod';
import { normaliseDecimal } from './money.js';
import { idSchema, isoDateSchema, optionalText, requiredText } from './validation.js';
/** The table of Tabelle with the areas of the progetti: empty, filled by the CFO. */
export const PROJECT_AREAS_TABLE = 'aree-progetti';
/* --- Access by area ------------------------------------------------------------ */
export const AREA_LEVELS = ['read', 'write', 'admin'];
export const AREA_LEVEL_LABELS = {
    read: 'Vede',
    write: 'Modifica',
    admin: 'Amministra',
};
export const AREA_LEVEL_DESCRIPTIONS = {
    read: 'consulta i progetti dell’area',
    write: 'crea e modifica i progetti dell’area, i costi e gli indicatori',
    admin: 'in più annulla ed elimina i progetti dell’area',
};
/** Whether a level reaches another: «Amministra» includes «Modifica», which includes «Vede». */
export function areaLevelReaches(level, wanted) {
    if (!level)
        return false;
    return AREA_LEVELS.indexOf(level) >= AREA_LEVELS.indexOf(wanted);
}
/** The highest level among the areas of a user; null without any. */
export function highestAreaLevel(areas) {
    let best = null;
    for (const level of Object.values(areas)) {
        if (!best || AREA_LEVELS.indexOf(level) > AREA_LEVELS.indexOf(best))
            best = level;
    }
    return best;
}
export const areaAccessSchema = z
    .object({
    entries: z.array(z.object({ userId: idSchema, level: z.enum(AREA_LEVELS) })).max(200),
})
    .superRefine((body, context) => {
    const seen = new Set();
    body.entries.forEach((entry, index) => {
        if (seen.has(entry.userId)) {
            context.addIssue({
                code: 'custom',
                path: ['entries', index, 'userId'],
                message: 'Utente già nell’elenco',
            });
        }
        seen.add(entry.userId);
    });
});
/* --- Indicators ---------------------------------------------------------------- */
/** The outcome of an indicator, given when the progetto closes. */
export const INDICATOR_OUTCOMES = ['raggiunto', 'parziale', 'non_raggiunto'];
export const INDICATOR_OUTCOME_LABELS = {
    raggiunto: 'Raggiunto',
    parziale: 'Raggiunto in parte',
    non_raggiunto: 'Non raggiunto',
};
/** A measured value: «1.234,5», «-3», «0,75»; up to four decimals. */
const measureValue = z
    .string()
    .trim()
    .transform((value, context) => {
    const normalised = normaliseDecimal(value);
    if (normalised === null || !/^-?\d{1,13}(\.\d{1,4})?$/.test(normalised)) {
        context.addIssue({ code: 'custom', message: 'Numero non valido' });
        return z.NEVER;
    }
    return normalised;
});
export const indicatorSchema = z
    .object({
    name: requiredText(200),
    /** How it is measured: «conteggio sul sito», «secondi, PageSpeed». */
    method: optionalText(300),
    baseline: measureValue,
    target: measureValue,
    dueOn: isoDateSchema.nullable().default(null),
    outcome: z.enum(INDICATOR_OUTCOMES).nullable().default(null),
})
    .refine((body) => Number(body.baseline) !== Number(body.target), {
    path: ['target'],
    message: 'L’obiettivo è diverso dal valore di partenza',
});
export const measureSchema = z.object({
    measuredOn: isoDateSchema,
    value: measureValue,
    note: optionalText(300),
});
/**
 * How far a value is on the way from the start to the target, 0..1: it works
 * both ways, growing (0 → 25 pages) and going down (4,2 → 1,5 seconds).
 */
export function indicatorProgress(baseline, target, value) {
    if (value === null)
        return null;
    const start = Number(baseline);
    const end = Number(target);
    if (start === end)
        return null;
    const share = (Number(value) - start) / (end - start);
    return Math.min(1, Math.max(0, share));
}
/** «25.0000» → «25», «1.5000» → «1.5»: a stored number without its trailing zeros. */
export function trimMeasure(value) {
    return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}
/** «1234.5» → «1.234,5»: the numbers of the indicators as the users read them. */
export function formatMeasure(value) {
    const [whole, decimals] = trimMeasure(value).replace(/^-/, '').split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${value.startsWith('-') ? '-' : ''}${grouped}${decimals ? `,${decimals}` : ''}`;
}
/**
 * Whether a user reaches the routes of commesse and progetti at a level
 * (ADR-025): with the module «Commesse e progetti», or for the progetti with an
 * area given to them or as PM of one. Each commessa or progetto is then checked
 * on its own.
 */
export function reachesJobs(user, level) {
    if (user.permissions.includes(`commesse.${level}`))
        return true;
    if (areaLevelReaches(highestAreaLevel(user.projectAreas), level))
        return true;
    // The PM sees and updates their own progetto.
    return user.projectPm && level !== 'admin';
}
//# sourceMappingURL=projects.js.map